import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { TOGGLE_LEFT_MENU } from 'store/layout/actions'
import AnalyticsFilterBar, { FilterField, FilterSelect } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsFilters from 'pages/main/analytics/parts/useAnalyticsFilters'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildPeriod } from 'pages/main/analytics/parts/analyticsDates'
import useChainDragScroll from './useChainDragScroll'

/**
 * Обёртка загрузки отчёта: отслеживает, идёт ли расчёт. Сетевой отчёт по месяцу и всем
 * филиалам при первом открытии периода может считаться десятки секунд — глобальный лоадер
 * на это время закрывал весь кабинет, и экран выглядел зависшим. Теперь экран живой, а панель
 * показывает полосу «Считаем отчёт…» (loading). Основной запрос экрана шлётся с { loader: false }.
 */
export function useChainLoad(load) {
	const [loading, setLoading] = useState(false)
	const lastRun = useRef(0)

	async function run(filter) {
		const id = ++lastRun.current
		setLoading(true)
		try {
			return await load(filter)
		} finally {
			// Ответ устаревшего запроса не снимает индикатор свежего
			if (id === lastRun.current) setLoading(false)
		}
	}

	return [run, loading]
}

/**
 * Состояние фильтра сетевого отчёта: период + набор филиалов (pos_ids, пусто — все доступные)
 * + поля экрана. Филиалы и период общие для всех сетевых отчётов и аналитики (sessionStorage).
 */
export function useChainFilters(defaults, load) {
	const [run, loading] = useChainLoad(load)
	const filters = useAnalyticsFilters({
		...buildPeriod('30days'),
		pos_ids: [],
		...defaults,
	}, run)
	return { ...filters, loading }
}

/** Полоса «Считаем отчёт…» под панелью фильтров, пока идёт расчёт. */
export function ChainLoadingBar({ loading }) {
	const { t } = useTranslation()

	// Признак на body: пустые таблицы пишут «Загружаем…» вместо «Нет данных», старые цифры приглушены
	useEffect(() => {
		document.body.classList.toggle('chain-is-loading', !!loading)
		return () => document.body.classList.remove('chain-is-loading')
	}, [loading])

	if (!loading) return null
	return (
		<div className="chain-loading mb-3">
			<div className="chain-loading-track"><span></span></div>
			<div className="chain-loading-text">
				<span className="spinner-border spinner-border-sm me-2"></span>
				{t('chain_loading')}
			</div>
		</div>
	)
}

/** Кнопка выгрузки — одинаковая на всех экранах. */
export function ExcelButton({ onClick, disabled }) {
	return (
		<button type="button" className="btn btn-sm btn-outline-success chain-excel-btn" style={{ borderRadius: '10px', fontWeight: 600 }}
			disabled={disabled} onClick={onClick}>
			<i className="uil uil-file-download-alt me-1"></i>
			Excel
		</button>
	)
}

/**
 * Панель фильтров сетевого отчёта: AnalyticsFilterBar + мультивыбор филиалов первым полем.
 * wide — экран с широкой матрицей «строка × филиал»: при открытии сворачиваем левое меню.
 */
function ChainFilterBar({ filters, title, icon, subtitle, actions, onExcel, excelDisabled, presets, tabs, activeTab, onTabChange, branches = true, wide = false, children }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const options = useAnalyticsOptions([])
	useChainDragScroll()
	const selected = filters.applied.pos_ids || []

	useEffect(() => {
		if (wide) dispatch(TOGGLE_LEFT_MENU(true))
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const branchesLabel = selected.length === 0
		? t('chain_all_branches')
		: `${t('chain_branches')}: ${selected.length}`

	return (
		<>
			<AnalyticsFilterBar
				title={title}
				icon={icon}
				subtitle={branches ? (subtitle ? <>{branchesLabel} · {subtitle}</> : branchesLabel) : subtitle}
				filters={filters}
				presets={presets}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={onTabChange}
				actions={
					<>
						{actions}
						{onExcel && <ExcelButton onClick={onExcel} disabled={excelDisabled} />}
					</>
				}
			>
				{branches && (
					<FilterField label={t('chain_branches')} wide>
						<FilterSelect
							isMulti
							options={options.poses}
							allLabel={t('chain_all_branches')}
							value={filters.draft.pos_ids}
							getOptionLabel={(option) => option.short_name || option.name}
							onChange={(ids) => filters.patch({ pos_ids: ids })}
						/>
					</FilterField>
				)}
				{children}
			</AnalyticsFilterBar>
			<ChainLoadingBar loading={filters.loading} />
		</>
	)
}

export { FilterField, FilterSelect }

export default ChainFilterBar
